/**
 * Analytics tracking module for The Fashion Detective.
 *
 * Safe when IDs are empty — all calls are no-ops when unconfigured.
 * Loads GA4 and Meta Pixel scripts lazily from /api/config.
 * Tracks: upload events, checkout events, page views.
 */

import api from '@/lib/api';

let ga4Id = '';
let metaPixelId = '';
let initialized = false;

/**
 * Best-effort POST to the first-party event store (GAP-01).
 *
 * Always fired, independent of GA4/Meta config, so internal funnel reporting
 * works even when third-party IDs are unset. Fully non-blocking: failures are
 * swallowed so analytics never disrupts the UI. Unknown event types are
 * rejected server-side.
 */
export function emitEvent(eventType, payload = {}) {
  try {
    api.post('/events', { event_type: eventType, ...payload }).catch(() => {});
  } catch {
    /* never let analytics break the page */
  }
}

/**
 * Initialize analytics from the app config.
 * Call once after fetching /api/config.
 */
export function initAnalytics(config) {
  if (initialized) return;
  initialized = true;

  ga4Id = config?.analytics?.ga4_id || '';
  metaPixelId = config?.analytics?.meta_pixel_id || '';

  // Load GA4 if configured
  if (ga4Id) {
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${ga4Id}`;
    script.async = true;
    document.head.appendChild(script);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', ga4Id, { send_page_view: false });
  }

  // Load Meta Pixel if configured
  if (metaPixelId) {
    window.fbq = window.fbq || function() { (window.fbq.q = window.fbq.q || []).push(arguments); };
    window.fbq('init', metaPixelId);
    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    script.async = true;
    document.head.appendChild(script);
  }
}

/**
 * Track a page view.
 *
 * `emitFirstParty` (default true) controls the first-party /events beacon.
 * ANL-02: the landing page's first-party page_view is already emitted by
 * AnalyticsListener on mount — before /config resolves and the GA4/Meta scripts
 * exist. To replay that initial view to GA4/Meta once they're configured (see
 * trackInitialPageView), pass emitFirstParty=false so the first-party event
 * isn't double-counted.
 */
export function trackPageView(path, emitFirstParty = true) {
  if (ga4Id && window.gtag) {
    window.gtag('event', 'page_view', { page_path: path });
  }
  if (metaPixelId && window.fbq) {
    window.fbq('track', 'PageView');
  }
  if (emitFirstParty) {
    emitEvent('page_view', { metadata: { path } });
  }
}

/**
 * ANL-02: replay the current page to GA4/Meta after analytics init.
 *
 * AnalyticsListener fires the FIRST page_view on mount, but that runs before the
 * async /config fetch resolves and initAnalytics() loads gtag/fbq — so the
 * landing-page view never reaches GA4/Meta. Call this once, right after
 * initAnalytics(), to send that first view to the third-party pixels. The
 * first-party beacon is suppressed (it already fired) to avoid double-counting.
 */
export function trackInitialPageView(path) {
  if (!ga4Id && !metaPixelId) return;
  trackPageView(path, false);
}

/**
 * Track the moment an image upload BEGINS (funnel start). First-party only —
 * GA4/Meta record the completed upload below.
 */
export function trackUploadStarted(category, slotKey) {
  emitEvent('upload_started', { metadata: { category, slot: slotKey } });
}

/**
 * Track a completed image upload event (for funnel analytics).
 */
export function trackUpload(category, slotKey) {
  if (ga4Id && window.gtag) {
    window.gtag('event', 'upload_image', { category, slot: slotKey });
  }
  if (metaPixelId && window.fbq) {
    window.fbq('trackCustom', 'UploadImage', { category, slot: slotKey });
  }
  emitEvent('upload_completed', { metadata: { category, slot: slotKey } });
}

/**
 * Track a checkout/purchase event.
 */
export function trackCheckout(orderNumber, totalCents, itemCount) {
  const value = totalCents / 100;
  if (ga4Id && window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: orderNumber,
      value,
      currency: 'USD',
      items: [{ quantity: itemCount }],
    });
  }
  if (metaPixelId && window.fbq) {
    window.fbq('track', 'Purchase', {
      value,
      currency: 'USD',
      content_type: 'product',
      num_items: itemCount,
    });
  }
  // NOTE: the authoritative payment_succeeded event is emitted server-side at
  // finalization (GAP-01). This client beacon mirrors GA4/Meta purchase only.
}

/**
 * Track adding item to cart.
 */
export function trackAddToCart(serviceFamily, category) {
  if (ga4Id && window.gtag) {
    window.gtag('event', 'add_to_cart', { service: serviceFamily, category });
  }
  if (metaPixelId && window.fbq) {
    window.fbq('track', 'AddToCart', { content_category: category });
  }
  emitEvent('item_added', { metadata: { service_family: serviceFamily, category } });
}

/**
 * Track an add-on selection (expedited / mailed document).
 */
export function trackAddonSelected(addonType, category) {
  emitEvent('addon_selected', { metadata: { addon_type: addonType, category } });
}

export default {
  initAnalytics, trackPageView, trackInitialPageView, trackUploadStarted, trackUpload,
  trackCheckout, trackAddToCart, trackAddonSelected, emitEvent,
};
